import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, User as UserIcon, Phone, FileText, Loader2, Clock, ChevronRight, AlertTriangle,
  Package, Plus, Trash2, RotateCcw, ExternalLink, Link2, Briefcase, Wrench, ClipboardList,
  Pencil, Save, X, CalendarClock, Route,
  CheckCircle2, Printer, Download, MessageSquare,
} from "lucide-react";
import OrderMapView from "../components/OrderMapView";
import PageForm from "../components/PageForm";
import SlideOverForm, { SlideOverSection } from "../components/SlideOverForm";
import AddressMapPicker from "../components/AddressMapPicker";
import { Tag, DollarSign, Wallet } from "lucide-react";

const STATUS_LABELS = {
  pendiente_agendamiento: "Pte. agendamiento",
  asignada: "Asignada",
  en_ruta: "En ruta",
  en_ejecucion: "Ejecutando",
  pendiente_fiscalizacion: "Pte. fiscalización",
  validada: "Validada",
  con_observaciones: "Con observaciones",
  rechazada: "Rechazada",
  prefacturada: "Prefacturada",
  cerrada: "Cerrada",
  cancelada: "Cancelada",
  agendada: "Asignada",
  en_sitio: "Ejecutando",
};

export default function OrdenDetalle() {
  const { id } = useParams();
  const { user, hasPerm } = useAuth();
  const navigate = useNavigate();
  const [zonesMap, setZonesMap] = useState({}); // { zone_id → {name, branch_name} }
  const [branchesMap, setBranchesMap] = useState({});
  const [order, setOrder] = useState(null);
  const [history, setHistory] = useState([]);
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [transitioning, setTransitioning] = useState(false);
  const [reason, setReason] = useState("");
  const [editingOrder, setEditingOrder] = useState(false);
  const [technicianRoute, setTechnicianRoute] = useState([]); // otras OTs del técnico este día
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentSaving, setCommentSaving] = useState(false);

  const canEdit = hasPerm("M1.edit");
  // v2.8.8.8 · Nuevos permisos granulares por acción
  const canValidate = hasPerm("M1.validate");
  const canPrint = hasPerm("M1.print");
  const canExport = hasPerm("M1.export");
  const canComment = hasPerm("M1.comment");
  const canViewMap = hasPerm("M1.view_map");
  const canViewHistory = hasPerm("M1.view_history");

  // Estados en los que se puede editar campos operativos de la OT
  const EDITABLE_STATES = ["pendiente_agendamiento", "asignada", "con_observaciones"];
  const RESTRICTED_STATES = ["en_ruta", "en_ejecucion", "pendiente_fiscalizacion", "rechazada"];
  const canEditOrder =
    canEdit && order &&
    (EDITABLE_STATES.includes(order.status) ||
      (RESTRICTED_STATES.includes(order.status) &&
        ["super_admin", "admin_zone", "supervisor", "agendador", "scheduler"].includes(user?.role)));

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [o, h, a] = await Promise.all([
        api.get(`/v1/work-orders/${id}`),
        api.get(`/v1/work-orders/${id}/history`),
        api.get(`/v1/work-orders/${id}/transitions/available`),
      ]);
      setOrder(o.data);
      setHistory(h.data.items || []);
      setAvailable(a.data.available || []);
    } catch (e) {
      toast.error("Error al cargar la orden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [id]);

  // Cargar ruta del técnico: otras OTs asignadas al mismo técnico el mismo día
  useEffect(() => {
    if (!order?.technician_id) {
      setTechnicianRoute([]);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/v1/work-orders", {
          params: {
            technician_id: order.technician_id,
            page_size: 30,
          },
        });
        const all = (data?.items || []).filter((o) => o.lat != null && o.lon != null);
        // Si tiene fecha planificada, filtrar mismo día
        const targetDate = order.scheduled_at ? new Date(order.scheduled_at).toDateString() : null;
        const filtered = targetDate
          ? all.filter((o) => o.scheduled_at && new Date(o.scheduled_at).toDateString() === targetDate)
          : all.slice(0, 8);
        setTechnicianRoute(filtered);
      } catch {
        setTechnicianRoute([]);
      }
    })();
  }, [order?.technician_id, order?.scheduled_at]);

  // Cargar zonas y sucursales una sola vez para poder mapear id → nombre
  useEffect(() => {
    (async () => {
      try {
        const [z, b] = await Promise.all([
          api.get("/v1/config/zones"),
          api.get("/v1/config/branches"),
        ]);
        const zm = {}; (z.data.items || []).forEach((it) => { zm[it.id] = it; if (it.code) zm[it.code] = it; });
        const bm = {}; (b.data.items || []).forEach((it) => { bm[it.id] = it; if (it.code) bm[it.code] = it; });
        setZonesMap(zm);
        setBranchesMap(bm);
      } catch { /* ignore */ }
    })();
  }, []);

  const transition = async (to) => {
    if (to === "cancelada" && !reason.trim()) {
      toast.error("Cancelar requiere un motivo");
      return;
    }

    // ── Geo-fence 500m para cierre de actividad ────────────
    // Al pasar a pendiente_fiscalizacion (cierre técnico), se solicitan
    // coords GPS del navegador y se envían al backend para validación.
    const payload = { to_status: to, reason: reason || undefined };
    if (to === "pendiente_fiscalizacion" && order?.lat && order?.lon) {
      if (!navigator.geolocation) {
        toast.error("Tu navegador no soporta geolocalización. No puedes cerrar la actividad.");
        return;
      }
      setTransitioning(true);
      try {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 0,
          });
        });
        payload.tech_lat = pos.coords.latitude;
        payload.tech_lon = pos.coords.longitude;
      } catch (err) {
        setTransitioning(false);
        toast.error("No se pudo obtener tu ubicación GPS. Acepta el permiso en el navegador para cerrar.");
        return;
      }
    }

    setTransitioning(true);
    try {
      await api.post(`/v1/work-orders/${id}/transition`, payload);
      toast.success(`Estado cambiado a ${STATUS_LABELS[to]}`);
      setReason("");
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cambiar el estado");
    } finally {
      setTransitioning(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Detalle de orden">
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gray-400" /></div>
      </Layout>
    );
  }
  if (!order) return null;

  const slaDue = order.sla_due_at ? new Date(order.sla_due_at) : null;
  const isOverdue = slaDue && slaDue < new Date() && !["cerrada", "validada", "cancelada"].includes(order.status);

  return (
    <Layout title={`Orden ${order.code}`} subtitle="Detalle · M1">
      <button onClick={() => navigate("/ordenes")} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4" data-testid="btn-back-orders">
        <ArrowLeft size={14} /> Volver a Órdenes
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left col */}
        <div className="lg:col-span-2 space-y-6">
          {/* Datos */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Código</div>
                <div className="text-2xl font-bold font-mono">{order.code}</div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <PriorityBadge priority={order.priority} />
                <span className="text-xs px-3 py-1 rounded-full bg-[#0A3747] text-[#E0E621] font-mono uppercase tracking-wider">
                  {STATUS_LABELS[order.status]}
                </span>
                {canEditOrder && (
                  <button
                    onClick={() => setEditingOrder(true)}
                    className="text-xs px-3 py-1.5 rounded bg-[#E0E621] text-[#0A3747] font-medium hover:brightness-95 flex items-center gap-1.5"
                    data-testid="edit-order-btn"
                    title="Editar técnico asignado, tipo de solución y fecha planificada"
                  >
                    <Pencil size={12} /> Editar
                  </button>
                )}
              </div>
            </div>

            <Section title="Cliente" icon={UserIcon}>
              <DataRow label="Nombres completos" value={order.client_name} />
              {order.client_document && <DataRow label="Documento" value={order.client_document} />}
              {order.client_phone && (
                <DataRow
                  label="Móvil del cliente"
                  value={
                    <a href={`tel:${order.client_phone}`} className="text-[#0A3747] hover:underline inline-flex items-center gap-1" data-testid="client-phone-link">
                      <Phone size={12} /> {order.client_phone}
                    </a>
                  }
                />
              )}
              {order.subscription_ref && (
                <DataRow
                  label="Suscripción vinculada"
                  value={
                    <span className="inline-flex items-center gap-1.5">
                      <Link2 size={12} className="text-[#0A3747]" />
                      <span className="font-mono text-xs">{order.subscription_ref}</span>
                    </span>
                  }
                />
              )}
            </Section>

            <Section title="Servicio" icon={ClipboardList}>
              <DataRow label="Tipo" value={order.type} capitalize />
              {order.service && <DataRow label="Plan" value={order.service} />}
              <DataRow label="Zona" value={zonesMap[order.zone_id]?.name || order.zone_name || order.zone_id?.replace("zone-", "") || "—"} capitalize />
              {(order.branch_id || order.branch_name) && (
                <DataRow label="Sucursal" value={branchesMap[order.branch_id]?.name || order.branch_name || order.branch_id} />
              )}
              {order.solution_type_name && (
                <DataRow
                  label="Tipo de Solución"
                  value={
                    <span className="text-sm font-medium">{order.solution_type_name}</span>
                  }
                />
              )}
              {/* v1.9.5 · Bug QA 22-07 #2: siempre mostrar Técnico (asignado o Sin asignar) */}
              <DataRow
                label="Técnico asignado"
                value={
                  order.technician_name ? (
                    <span className="text-sm font-medium inline-flex items-center gap-1.5">
                      <UserIcon size={12} className="text-emerald-700" />
                      {order.technician_name}
                    </span>
                  ) : (
                    <span className="text-sm italic text-slate-400">— Sin asignar —</span>
                  )
                }
              />
              {order.scheduled_at && (
                <DataRow
                  label="Fecha planificada"
                  value={
                    <span className="text-sm font-medium inline-flex items-center gap-1.5">
                      <CalendarClock size={12} className="text-[#0A3747]" />
                      {new Date(order.scheduled_at).toLocaleString("es-EC")}
                    </span>
                  }
                />
              )}
            </Section>

            {order.type === "instalacion" && (order.salesman_name || order.salesman_phone) && (
              <Section title="Vendedor" icon={Briefcase}>
                {order.salesman_name && <DataRow label="Nombre" value={order.salesman_name} />}
                {order.salesman_phone && (
                  <DataRow
                    label="Móvil del vendedor"
                    value={
                      <a href={`tel:${order.salesman_phone}`} className="text-[#0A3747] hover:underline inline-flex items-center gap-1" data-testid="salesman-phone-link">
                        <Phone size={12} /> {order.salesman_phone}
                      </a>
                    }
                  />
                )}
              </Section>
            )}

            <Section title="Ubicación" icon={MapPin}>
              <DataRow label="Dirección" value={order.address} />
              {order.lat != null && order.lon != null && (
                <DataRow
                  label="Coordenadas"
                  value={
                    <a
                      href={`https://www.google.com/maps?q=${order.lat},${order.lon}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#0A3747] hover:underline inline-flex items-center gap-1.5 font-mono text-xs"
                      title="Abrir en Google Maps"
                      data-testid="coords-link"
                    >
                      <MapPin size={12} />
                      {Number(order.lat).toFixed(6)}, {Number(order.lon).toFixed(6)}
                      <ExternalLink size={11} className="opacity-60" />
                    </a>
                  }
                />
              )}
              {/* Mapa: cliente + zona georreferenciada + ruta del técnico
                  · v2.8.8.8: oculto por hasPerm(M1.view_map) */}
              {canViewMap && (
              <div className="mt-3" data-testid="section-mapa-ot">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
                    <Route size={11} />
                    {order.technician_id && technicianRoute.length > 1
                      ? `Ruta del técnico (${technicianRoute.length} paradas)`
                      : "Mapa del punto"}
                  </div>
                  {/* v1.9.6 · Bug QA 23-07 M8: renombrar 'Zona georref' a algo más claro */}
                  {order.zone_id && zonesMap[order.zone_id]?.geometry_type && (
                    <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
                      Zona operativa: {zonesMap[order.zone_id].name}
                    </span>
                  )}
                </div>
                <OrderMapView
                  order={{
                    ...order,
                    zone_name: order.zone_id && zonesMap[order.zone_id]?.name,
                  }}
                  zone={order.zone_id ? zonesMap[order.zone_id] : null}
                  technicianRoute={technicianRoute}
                  height={280}
                />
              </div>
              )}
            </Section>

            {order.type === "instalacion" && order.installation_observations && (
              <Section title="Observaciones para la instalación" icon={Wrench}>
                <p className="text-sm text-gray-700 whitespace-pre-wrap" data-testid="install-obs">
                  {order.installation_observations}
                </p>
              </Section>
            )}

            {order.description_html && (
              <Section title="Descripción técnica" icon={FileText}>
                <div
                  className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none"
                  data-testid="description-html-view"
                  dangerouslySetInnerHTML={{ __html: order.description_html }}
                />
              </Section>
            )}

            {order.observations && (
              <Section title="Observaciones generales">
                <p className="text-sm text-gray-700">{order.observations}</p>
              </Section>
            )}
          </div>

          {/* History · v2.8.8.8: oculto por hasPerm(M1.view_history) */}
          {canViewHistory && (
          <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="section-history-ot">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "Space Grotesk" }}>
              <Clock size={16} className="text-[#9BB300]" />
              Histórico y bitácora
            </h3>
            <div className="space-y-3">
              {history.map((h, idx) => {
                const isComment = h.kind === "comment";
                return (
                  <div key={h.id || idx} className="flex gap-3" data-testid={`history-${idx}`}>
                    <div className="flex flex-col items-center">
                      <div className={`w-2.5 h-2.5 rounded-full ring-4 ${isComment ? "bg-[#0A3747] ring-[#0A3747]/20" : "bg-[#E0E621] ring-[#E0E621]/20"}`} />
                      {idx < history.length - 1 && <div className="w-px flex-1 bg-gray-200 my-1" />}
                    </div>
                    <div className="flex-1 pb-3">
                      {isComment ? (
                        <>
                          <div className="flex items-center gap-2 flex-wrap">
                            <MessageSquare size={12} className="text-[#0A3747]" />
                            <span className="text-sm font-medium text-[#0A3747]">Comentario</span>
                            {h.by_user_name && (
                              <span className="text-[10px] text-gray-500">· {h.by_user_name}</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-700 mt-1 whitespace-pre-wrap" data-testid={`comment-note-${idx}`}>
                            {h.note}
                          </div>
                          <div className="text-[10px] font-mono text-gray-400 mt-0.5">
                            {new Date(h.at).toLocaleString("es-EC")}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium">
                              {h.from_status ? `${STATUS_LABELS[h.from_status] || h.from_status}` : "Inicio"}
                            </span>
                            <ChevronRight size={12} className="text-gray-400" />
                            <span className="text-sm font-medium text-[#0A3747]">{STATUS_LABELS[h.to_status] || h.to_status}</span>
                          </div>
                          {h.reason && <div className="text-xs text-gray-600 mt-0.5">{h.reason}</div>}
                          <div className="text-[10px] font-mono text-gray-400 mt-0.5">
                            {new Date(h.at).toLocaleString("es-EC")} · {h.actor_role || "—"}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          )}
        </div>

        {/* Right col */}
        <div className="space-y-6">
          {/* SLA */}
          <div className={`rounded-lg p-5 ${isOverdue ? "bg-red-50 border border-red-200" : "bg-white border border-gray-200"}`} data-testid="sla-card">
            <div className="text-[10px] uppercase font-mono tracking-wider text-gray-500 mb-1">SLA</div>
            {slaDue ? (
              <>
                <div className={`text-2xl font-bold ${isOverdue ? "text-red-700" : "text-gray-900"}`} style={{ fontFamily: "Space Grotesk" }}>
                  {isOverdue ? "⚠ VENCIDO" : "Vigente"}
                </div>
                <div className="text-sm text-gray-600 mt-1">Vence: {slaDue.toLocaleString("es-EC")}</div>
              </>
            ) : <div className="text-gray-500">—</div>}
          </div>

          {/* Actions */}
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="font-bold text-gray-900 mb-3" style={{ fontFamily: "Space Grotesk" }}>Transiciones disponibles</h3>
            {available.length === 0 ? (
              <div className="text-sm text-gray-500">No hay transiciones disponibles.</div>
            ) : (
              <div className="space-y-2">
                {available.includes("cancelada") && (
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={2}
                    placeholder="Motivo (requerido al cancelar)"
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded mb-2 focus:ring-2 focus:ring-[#E0E621]"
                    data-testid="transition-reason"
                  />
                )}
                {available.map((target) => (
                  <button
                    key={target}
                    onClick={() => transition(target)}
                    disabled={transitioning}
                    className={`w-full px-3 py-2 rounded text-sm font-medium transition flex items-center justify-between ${
                      target === "cancelada"
                        ? "bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                        : "bg-[#0A3747] text-white hover:bg-[#135166]"
                    } disabled:opacity-40`}
                    data-testid={`transition-${target}`}
                  >
                    <span>{STATUS_LABELS[target] || target}</span>
                    {transitioning ? <Loader2 size={12} className="animate-spin" /> : <ChevronRight size={14} />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* v2.8.8.8 · Acciones rápidas granulares (M1.validate/print/export/comment) */}
          {(canValidate || canPrint || canExport || canComment) && (
            <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="section-quick-actions">
              <h3 className="font-bold text-gray-900 mb-3 text-sm uppercase tracking-widest font-mono" style={{ fontFamily: "JetBrains Mono" }}>Acciones rápidas</h3>
              <div className="grid grid-cols-2 gap-2">
                {canValidate && available.includes("validada") && (
                  <button
                    onClick={() => transition("validada")}
                    disabled={transitioning}
                    className="flex items-center justify-center gap-1 px-3 py-2 text-xs font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
                    data-testid="btn-validate-ot"
                    title="Validar y cerrar la OT"
                  >
                    <CheckCircle2 size={13}/> Validar/Cerrar
                  </button>
                )}
                {canPrint && (
                  <button
                    onClick={() => window.print()}
                    className="flex items-center justify-center gap-1 px-3 py-2 text-xs font-semibold rounded-md border border-gray-300 hover:bg-gray-50"
                    data-testid="btn-print-ot"
                    title="Imprimir orden de trabajo"
                  >
                    <Printer size={13}/> Imprimir
                  </button>
                )}
                {canExport && (
                  <button
                    onClick={() => {
                      const data = JSON.stringify(order, null, 2);
                      const blob = new Blob([data], { type: "application/json" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a"); a.href = url; a.download = `OT_${order.code || order.id}.json`;
                      document.body.appendChild(a); a.click(); document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                      toast.success("OT exportada");
                    }}
                    className="flex items-center justify-center gap-1 px-3 py-2 text-xs font-semibold rounded-md border border-gray-300 hover:bg-gray-50"
                    data-testid="btn-export-ot"
                    title="Exportar OT a JSON"
                  >
                    <Download size={13}/> Exportar
                  </button>
                )}
                {canComment && (
                  <button
                    onClick={() => { setCommentText(""); setCommentModalOpen(true); }}
                    className="flex items-center justify-center gap-1 px-3 py-2 text-xs font-semibold rounded-md border border-gray-300 hover:bg-gray-50"
                    data-testid="btn-comment-ot"
                    title="Agregar comentario a la bitácora de la OT"
                  >
                    <MessageSquare size={13}/> Comentar
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Materiales usados */}
          <MaterialsSection orderId={id} orderCode={order.code} canEdit={canEdit} />

          {/* RN-17 · Devolución de equipos (visible para todos, obligatoria en retiro/upgrade) */}
          <ReturnsSection orderId={id} orderType={order.type} canEdit={canEdit} />

          {/* Metadata */}
          <div className="bg-white border border-gray-200 rounded-lg p-5 text-xs space-y-2">
            <div className="text-[10px] uppercase font-mono tracking-wider text-gray-500 mb-2">Metadatos</div>
            <div><span className="text-gray-500">ID:</span> <span className="font-mono">{order.id.slice(0, 8)}</span></div>
            <div><span className="text-gray-500">Creada:</span> <span>{new Date(order.created_at).toLocaleString("es-EC")}</span></div>
            <div><span className="text-gray-500">Actualizada:</span> <span>{new Date(order.updated_at).toLocaleString("es-EC")}</span></div>
          </div>
        </div>
      </div>

      {/* Modal editar OT */}
      {editingOrder && (
        <EditOrderModal
          order={order}
          onClose={() => setEditingOrder(false)}
          onSaved={() => {
            setEditingOrder(false);
            fetchAll();
          }}
        />
      )}

      {/* Modal Comentar bitácora · v2.8.8.9 */}
      {commentModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => !commentSaving && setCommentModalOpen(false)}
          data-testid="comment-modal-backdrop"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-lg w-full max-w-md p-5 shadow-xl"
            data-testid="comment-modal"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-[#0A3747] flex items-center gap-2" style={{ fontFamily: "Space Grotesk" }}>
                <MessageSquare size={16} /> Agregar comentario a la bitácora
              </h3>
              <button
                onClick={() => setCommentModalOpen(false)}
                disabled={commentSaving}
                className="text-gray-500 hover:text-gray-800 text-xl leading-none disabled:opacity-40"
                data-testid="btn-close-comment-modal"
              >×</button>
            </div>
            <p className="text-[11px] text-gray-500 mb-3">
              El comentario quedará registrado en el histórico de la OT <span className="font-mono">{order.code}</span> con tu usuario y fecha.
            </p>
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              rows={4}
              placeholder="Escribe el comentario o nota interna…"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-[#E0E621] focus:border-transparent"
              data-testid="input-comment-text"
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => setCommentModalOpen(false)}
                disabled={commentSaving}
                className="px-4 py-2 border border-gray-300 text-sm rounded hover:bg-gray-50 disabled:opacity-40"
                data-testid="btn-cancel-comment"
              >Cancelar</button>
              <button
                type="button"
                onClick={async () => {
                  const note = commentText.trim();
                  if (!note) { toast.error("Escribe algún texto"); return; }
                  setCommentSaving(true);
                  try {
                    await api.post(`/v1/work-orders/${id}/comment`, { note });
                    toast.success("Comentario agregado a la bitácora");
                    setCommentModalOpen(false);
                    setCommentText("");
                    fetchAll();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "No se pudo guardar el comentario");
                  } finally {
                    setCommentSaving(false);
                  }
                }}
                disabled={commentSaving || !commentText.trim()}
                className="px-4 py-2 bg-[#0A3747] text-white text-sm rounded hover:bg-[#135166] disabled:opacity-40 flex items-center gap-1.5"
                data-testid="btn-submit-comment"
              >
                {commentSaving ? <Loader2 size={13} className="animate-spin" /> : <MessageSquare size={13} />}
                Guardar comentario
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="mb-4 last:mb-0">
      <h4 className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-gray-500 mb-2">
        {Icon && <Icon size={11} className="text-[#9BB300]" />}
        {title}
      </h4>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function DataRow({ label, value, capitalize, mono }) {
  return (
    <div className="flex items-start text-sm gap-3">
      <div className="w-28 text-gray-500 text-xs">{label}</div>
      <div className={`flex-1 text-gray-900 ${capitalize ? "capitalize" : ""} ${mono ? "font-mono text-xs" : ""}`}>{value}</div>
    </div>
  );
}

function PriorityBadge({ priority }) {
  const map = {
    P1: "bg-red-100 text-red-800",
    P2: "bg-yellow-100 text-yellow-800",
    P3: "bg-gray-100 text-gray-700",
  };
  return <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${map[priority]}`}>{priority}</span>;
}

function MaterialsSection({ orderId, orderCode, canEdit }) {
  const [data, setData] = useState({ items: [], active_count: 0, total_quantity: 0 });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/v1/work-orders/${orderId}/materials`);
      setData(r.data);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, [orderId]);

  const revert = async (matId) => {
    if (!window.confirm("¿Devolver este material al inventario?")) return;
    try {
      await api.delete(`/v1/work-orders/${orderId}/materials/${matId}`);
      toast.success("Material devuelto al inventario");
      fetch();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al revertir");
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="wo-materials-section">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase font-mono tracking-wider text-gray-500 flex items-center gap-1.5">
          <Package size={13} /> Materiales usados
        </div>
        {canEdit && (
          <button
            onClick={() => setAdding(true)}
            className="flex items-center gap-1 text-xs text-[#0A3747] hover:underline"
            data-testid="add-material-btn"
          >
            <Plus size={13} /> Agregar
          </button>
        )}
      </div>
      {loading ? (
        <div className="py-4 flex justify-center"><Loader2 size={16} className="animate-spin text-gray-400" /></div>
      ) : data.items.length === 0 ? (
        <div className="text-xs text-gray-500 italic py-2" data-testid="no-materials">
          Sin materiales registrados.
        </div>
      ) : (
        <div className="space-y-2">
          {data.items.map((m) => (
            <div
              key={m.id}
              className={`text-xs border rounded p-2 ${m.reverted_at ? "border-gray-200 bg-gray-50 opacity-60" : "border-gray-200"}`}
              data-testid={`material-row-${m.sku}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[10px] text-gray-500 truncate">{m.sku}</div>
                  <div className="font-medium truncate">{m.item_name}</div>
                  <div className="text-[10px] text-gray-500 mt-0.5">
                    {m.branch_name} · {new Date(m.used_at).toLocaleDateString("es-EC")}
                    {m.serial_number && <span className="ml-1">· S/N {m.serial_number}</span>}
                  </div>
                </div>
                <div className="text-right whitespace-nowrap">
                  <div className={`font-mono text-sm font-bold ${m.reverted_at ? "line-through" : "text-[#0A3747]"}`}>
                    {m.quantity} {m.unit}
                  </div>
                  {!m.reverted_at && canEdit && (
                    <button
                      onClick={() => revert(m.id)}
                      className="mt-1 text-[10px] text-red-600 hover:underline flex items-center gap-0.5"
                      data-testid={`revert-material-${m.sku}`}
                      title="Devolver al inventario"
                    >
                      <RotateCcw size={9} /> devolver
                    </button>
                  )}
                  {m.reverted_at && (
                    <div className="text-[9px] text-gray-500 italic mt-0.5">Devuelto</div>
                  )}
                </div>
              </div>
            </div>
          ))}
          <div className="pt-2 mt-2 border-t border-gray-100 text-[11px] text-gray-600 flex justify-between">
            <span>Consumos activos:</span>
            <span className="font-bold" data-testid="materials-total">{data.active_count}</span>
          </div>
        </div>
      )}

      {adding && (
        <AddMaterialModal
          orderId={orderId}
          orderCode={orderCode}
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); fetch(); }}
        />
      )}
    </div>
  );
}

function AddMaterialModal({ orderId, orderCode, onClose, onSaved }) {
  const [inv, setInv] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [serial, setSerial] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    api.get("/v1/inventory").then((r) => {
      setInv(r.data.items || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const selectedItem = inv.find((i) => i.id === selectedItemId);
  const branchOptions = selectedItem ? (selectedItem.stock_by_branch || []).filter((b) => b.quantity > 0) : [];
  const availableStock = branchOptions.find((b) => b.branch_id === selectedBranchId)?.quantity || 0;

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const qty = Number(quantity);
    if (!selectedItemId || !selectedBranchId || qty <= 0) {
      toast.error("Completa ítem, bodega y cantidad válida");
      return;
    }
    if (qty > availableStock) {
      toast.error(`Stock insuficiente: solo hay ${availableStock} disponibles`);
      return;
    }
    setSaving(true);
    try {
      await api.post(`/v1/work-orders/${orderId}/materials`, {
        inventory_item_id: selectedItemId,
        branch_id: selectedBranchId,
        quantity: qty,
        serial_number: serial || null,
        notes: notes || null,
      });
      toast.success(`Material consumido en ${orderCode}`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al agregar material");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="add-material-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-semibold text-sm flex items-center gap-2">
            <Package size={14} /> Consumir material
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded text-gray-500">×</button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3 text-sm">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5 uppercase tracking-wider">Ítem</label>
            <select
              value={selectedItemId}
              onChange={(e) => { setSelectedItemId(e.target.value); setSelectedBranchId(""); }}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              required
              disabled={loading}
              data-testid="material-item-select"
            >
              <option value="">— Seleccionar ítem —</option>
              {inv.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.sku} · {i.name} ({i.stock_total} {i.unit})
                </option>
              ))}
            </select>
          </div>
          {selectedItem && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5 uppercase tracking-wider">Bodega origen</label>
              <select
                value={selectedBranchId}
                onChange={(e) => setSelectedBranchId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                required
                data-testid="material-branch-select"
              >
                <option value="">— Seleccionar bodega —</option>
                {branchOptions.map((b) => (
                  <option key={b.branch_id} value={b.branch_id}>
                    {b.branch_name} ({b.quantity} {selectedItem.unit} disp.)
                  </option>
                ))}
              </select>
              {branchOptions.length === 0 && (
                <div className="text-[11px] text-red-600 mt-1">Sin stock disponible en ninguna bodega.</div>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5 uppercase tracking-wider">Cantidad</label>
              <input
                type="number" step="0.01" min="0.01"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                max={availableStock || undefined}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm font-mono text-right"
                data-testid="material-quantity"
              />
              {selectedItem && availableStock > 0 && (
                <div className="text-[10px] text-gray-500 mt-0.5">Máx {availableStock} {selectedItem.unit}</div>
              )}
            </div>
            {selectedItem?.has_serial && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5 uppercase tracking-wider">N° Serie</label>
                <input
                  type="text" value={serial}
                  onChange={(e) => setSerial(e.target.value)}
                  autoComplete="off" spellCheck={false}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm font-mono"
                  data-testid="material-serial"
                />
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5 uppercase tracking-wider">Notas (opcional)</label>
            <textarea
              value={notes} rows={2}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm resize-none"
              data-testid="material-notes"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <button
              type="submit" disabled={saving || !selectedItemId || !selectedBranchId}
              className="flex-1 px-4 py-2 bg-[#0A3747] text-white text-sm rounded hover:bg-[#135166] disabled:opacity-40 flex items-center justify-center gap-2"
              data-testid="save-material"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
              Consumir
            </button>
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 text-sm rounded hover:bg-gray-50">Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── RN-17 · Devolución obligatoria de equipos ───────────────────────────
const RETURN_CONDITIONS = [
  { code: "working", label: "Funcional" },
  { code: "damaged", label: "Dañado" },
  { code: "obsolete", label: "Obsoleto" },
  { code: "lost", label: "Perdido / no recuperado" },
];

function ReturnsSection({ orderId, orderType, canEdit }) {
  const [state, setState] = useState({ items: [], requires_return: false, compliant: true });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const fetchData = async () => {
    try {
      const r = await api.get(`/v1/work-orders/${orderId}/returns`);
      setState(r.data);
    } catch {
      /* silent */
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [orderId]);

  const removeReturn = async (rid) => {
    if (!window.confirm("¿Eliminar este registro de devolución?")) return;
    try {
      await api.delete(`/v1/work-orders/${orderId}/returns/${rid}`);
      toast.success("Devolución eliminada");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar");
    }
  };

  const requires = state.requires_return;
  const compliant = state.compliant;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="returns-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-md flex items-center justify-center ${requires && !compliant ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
            <RotateCcw size={16} />
          </div>
          <div>
            <div className="text-[10px] uppercase font-mono tracking-wider text-gray-500">RN-17 · Devolución de equipos</div>
            <div className="text-sm font-semibold text-gray-900">
              {requires ? "Obligatoria para este tipo de orden" : "Opcional (tipo de orden no lo exige)"}
            </div>
          </div>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0A3747] text-white text-xs rounded hover:bg-[#135166]"
            data-testid="add-return-btn"
          >
            <Plus size={12} /> Registrar devolución
          </button>
        )}
      </div>

      {requires && !compliant && (
        <div className="mb-3 flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-xs text-red-800" data-testid="return-warning">
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
          <div>
            <strong>Advertencia RN-17:</strong> esta orden es tipo <span className="font-mono">{orderType}</span> y aún no tiene devoluciones registradas.
            La transición a <em>Pte. fiscalización</em> quedará bloqueada hasta registrar al menos una.
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-4"><Loader2 className="animate-spin text-gray-400" size={18} /></div>
      ) : state.items.length === 0 ? (
        <div className="text-xs text-gray-500 italic py-2" data-testid="no-returns">Sin devoluciones registradas.</div>
      ) : (
        <div className="space-y-2">
          {state.items.map((r) => (
            <div key={r.id} className="flex items-start gap-3 p-3 rounded-md border border-gray-200 bg-gray-50" data-testid={`return-${r.id}`}>
              <div className="w-9 h-9 rounded-md bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                <Package size={16} className="text-[#0A3747]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs bg-[#0A3747] text-white px-2 py-0.5 rounded">{r.serial_number}</span>
                  <ConditionBadge condition={r.condition} />
                  {r.item_name && <span className="text-xs text-gray-600">· {r.item_name}</span>}
                </div>
                {r.notes && <div className="text-xs text-gray-600 mt-1.5">{r.notes}</div>}
                <div className="text-[10px] font-mono text-gray-400 mt-1">
                  {r.returned_by_name || "—"} · {new Date(r.returned_at).toLocaleString("es-EC")}
                </div>
              </div>
              {canEdit && (
                <button
                  onClick={() => removeReturn(r.id)}
                  className="p-1.5 rounded hover:bg-red-50 text-red-600"
                  title="Eliminar"
                  data-testid={`del-return-${r.id}`}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <ReturnModal orderId={orderId} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); fetchData(); }} />
      )}
    </div>
  );
}

function ConditionBadge({ condition }) {
  const map = {
    working:  { label: "Funcional", bg: "bg-emerald-100", text: "text-emerald-800" },
    damaged:  { label: "Dañado",    bg: "bg-amber-100",   text: "text-amber-800" },
    obsolete: { label: "Obsoleto",  bg: "bg-gray-200",    text: "text-gray-800" },
    lost:     { label: "Perdido",   bg: "bg-red-100",     text: "text-red-800" },
  };
  const m = map[condition] || map.obsolete;
  return <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${m.bg} ${m.text}`}>{m.label}</span>;
}

function ReturnModal({ orderId, onClose, onSaved }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ item_id: "", serial_number: "", condition: "working", notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/v1/inventory").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.serial_number || form.serial_number.length < 3) {
      toast.error("Serial requerido (mín 3 caracteres)"); return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.item_id) delete payload.item_id;
      await api.post(`/v1/work-orders/${orderId}/returns`, payload);
      toast.success("Devolución registrada");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="return-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg w-full max-w-md p-5 shadow-xl">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold" style={{ fontFamily: "Chivo" }}>Registrar devolución de equipo</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-xl leading-none">×</button>
        </div>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <div>
            <label className="block text-[10px] uppercase font-mono tracking-wider text-gray-500 mb-1">Serial *</label>
            <input
              value={form.serial_number}
              onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
              autoComplete="off" required minLength={3} maxLength={120}
              className="w-full px-3 py-2 border border-gray-300 rounded font-mono"
              data-testid="return-serial"
              placeholder="Ej: SN-CPE-001234"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase font-mono tracking-wider text-gray-500 mb-1">Condición *</label>
            <select
              value={form.condition}
              onChange={(e) => setForm({ ...form, condition: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded"
              data-testid="return-condition"
            >
              {RETURN_CONDITIONS.map((c) => (<option key={c.code} value={c.code}>{c.label}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase font-mono tracking-wider text-gray-500 mb-1">Ítem del catálogo (opcional)</label>
            <select
              value={form.item_id}
              onChange={(e) => setForm({ ...form, item_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded"
              data-testid="return-item"
            >
              <option value="">— sin catálogo —</option>
              {items.slice(0, 200).map((i) => (
                <option key={i.id} value={i.id}>{i.sku} · {i.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase font-mono tracking-wider text-gray-500 mb-1">Observaciones</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              data-testid="return-notes"
              placeholder="Estado físico, motivo del retiro, etc."
            />
          </div>
          <div className="flex gap-2 pt-2">
            <button
              type="submit" disabled={saving}
              className="flex-1 px-4 py-2 bg-[#0A3747] text-white text-sm rounded hover:bg-[#135166] disabled:opacity-40 flex items-center justify-center gap-2"
              data-testid="save-return-btn"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
              Registrar
            </button>
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 text-sm rounded hover:bg-gray-50">Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ─── Modal de edición de OT ─────────────────────────────────────────────
// Permite editar técnico asignado, tipo de solución y fecha planificada
// en OTs en estado "nuevo" (pendiente_agendamiento), asignada o con_observaciones.
function EditOrderModal({ order, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [techs, setTechs] = useState([]);
  const [solutionTypes, setSolutionTypes] = useState([]);
  const [treeRoots, setTreeRoots] = useState([]); // categorías raíz del árbol de solución
  const [effectiveRules, setEffectiveRules] = useState(null); // reglas efectivas del nodo seleccionado
  const [loadingRefs, setLoadingRefs] = useState(true);

  const toLocalInputValue = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
      return "";
    }
  };

  const [form, setForm] = useState({
    // Cliente
    client_name: order.client_name || "",
    client_phone: order.client_phone || "",
    client_document: order.client_document || "",
    address: order.address || "",
    lat: order.lat || "",
    lon: order.lon || "",
    // Programación
    technician_id: order.technician_id || "",
    scheduled_at: toLocalInputValue(order.scheduled_at),
    priority: order.priority || "P2",
    // Solución (jerarquía 3 niveles: root → service → leaf)
    solution_tree_root_id: order.solution_tree_root_id || "",
    solution_tree_service_id: order.solution_tree_service_id || "",
    solution_type_id: order.solution_type_id || "",
    // Sucursal / zona
    branch_id: order.branch_id || "",
    zone_id: order.zone_id || "",
    // Financiero
    pvp_amount: order.pvp_amount ?? "",
    // Notas
    observations: order.observations || "",
    installation_observations: order.installation_observations || "",
  });

  useEffect(() => {
    (async () => {
      setLoadingRefs(true);
      try {
        const branchId = order.branch_id || "";
        const zoneId = order.zone_id || "";
        const params = new URLSearchParams();
        if (branchId) params.set("branch_id", branchId);
        if (zoneId) params.set("zone_id", zoneId);
        const [uRes, sRes, tRes] = await Promise.all([
          api.get(`/v1/users/technicians?${params.toString()}`).catch(() =>
            api.get("/v1/users?page_size=500").catch(() => ({ data: { items: [] } }))
          ),
          api.get("/v1/solution-types").catch(() => ({ data: { items: [] } })),
          api.get("/v1/solution-tree").catch(() => ({ data: { tree: [] } })),
        ]);
        setTechs(uRes?.data?.items || []);
        setSolutionTypes(sRes?.data?.items || []);
        setTreeRoots(tRes?.data?.tree || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingRefs(false);
      }
    })();
  }, []);

  // Cargar reglas efectivas cuando cambia el tipo de solución (bloque de cascada v1.7.9)
  const stId = form.solution_type_id;
  useEffect(() => {
    if (!stId) { setEffectiveRules(null); return; }
    // Si el ID corresponde a un nodo del árbol N-nivel, pedir cascada
    api.get(`/v1/solution-tree/${stId}/effective-rules`)
      .then(({ data }) => setEffectiveRules(data))
      .catch(() => setEffectiveRules(null));
  }, [stId]);

  // v1.9.4 · Auto-detectar root + service cuando llega el árbol y ya hay un solution_type_id
  useEffect(() => {
    if (!form.solution_type_id || treeRoots.length === 0) return;
    if (form.solution_tree_root_id && form.solution_tree_service_id) return;
    // Busca en qué root/service vive el solution_type_id
    for (const root of treeRoots) {
      for (const svc of (root.children || [])) {
        const findLeaf = (node) => {
          if (!node) return false;
          if (node.id === form.solution_type_id) return true;
          return (node.children || []).some(findLeaf);
        };
        if (findLeaf(svc)) {
          setForm((f) => ({
            ...f,
            solution_tree_root_id: f.solution_tree_root_id || root.id,
            solution_tree_service_id: f.solution_tree_service_id || svc.id,
          }));
          return;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeRoots, form.solution_type_id]);

  // v1.9.4 · Jerarquía de 3 niveles: root (línea de negocio) → service → activity/leaf
  // Bug 23-Feb: dropdown plano de 54 items es inusable. Se agrega nivel intermedio.
  const LOB_TO_ROOT = {
    operaciones_clientes: "OPERACIONES DE CLIENTES",
    corp_seguridad: "CORPORATIVO - SEGURIDAD TI",
    corp_servidores_nube: "CORPORATIVO - SERVIDORES Y NUBE",
    corp_redes: "CORPORATIVO - REDES",
    red_pasiva: "RED PASIVA (PLANTA EXTERNA)",
    red_activa: "RED ACTIVA (NETWORKING)",
    infra_fisica: "INFRAESTRUCTURA FÍSICA",
  };

  // Root seleccionado
  const selectedRoot = useMemo(() => {
    return treeRoots.find((r) => r.id === form.solution_tree_root_id) || null;
  }, [treeRoots, form.solution_tree_root_id]);

  // Nivel 2: Servicios (hijos directos del root) — filtrados por root elegido
  const servicesOfRoot = useMemo(() => {
    if (!selectedRoot) return [];
    return (selectedRoot.children || []).filter((n) => n.active !== false);
  }, [selectedRoot]);

  // Recolecta TODOS los descendientes hoja de un nodo del árbol
  const collectLeaves = (node) => {
    const out = [];
    const walk = (n) => {
      if (!n || n.active === false) return;
      const kids = (n.children || []).filter((k) => k.active !== false);
      if (kids.length === 0) {
        // hoja
        out.push({ id: n.id, code: n.code || `NODE-${n.id.slice(0, 6)}`, name: n.name, _fromTree: true });
      } else {
        kids.forEach(walk);
      }
    };
    walk(node);
    return out;
  };

  // Nivel 3: Actividades específicas (hojas descendientes del service o del root si no hay service)
  const filteredSolutionTypes = useMemo(() => {
    if (!form.solution_tree_root_id) return solutionTypes;
    if (!selectedRoot) return solutionTypes;

    // Legacy filtrados por line_of_business ↔ nombre de raíz
    const rootName = (selectedRoot.name || "").toUpperCase();
    const legacyFiltered = solutionTypes.filter((s) => {
      const lob = String(s.line_of_business || s.linea_negocio || "").toLowerCase();
      const mappedRoot = LOB_TO_ROOT[lob];
      if (mappedRoot && mappedRoot.toUpperCase() === rootName) return true;
      const path = ((s.solution_tree_path || []).join(" ") || "").toUpperCase();
      return path.includes(rootName);
    });

    // Hojas del árbol N-nivel
    let treeLeaves = [];
    if (form.solution_tree_service_id) {
      const svc = servicesOfRoot.find((s) => s.id === form.solution_tree_service_id);
      if (svc) treeLeaves = collectLeaves(svc);
    } else {
      // Sin servicio elegido → hojas de todos los servicios del root
      servicesOfRoot.forEach((svc) => {
        treeLeaves = treeLeaves.concat(collectLeaves(svc));
      });
      // También el propio root si es una hoja directa (sin hijos)
      if (servicesOfRoot.length === 0) {
        treeLeaves = collectLeaves(selectedRoot);
      }
    }

    // Combinar: si hay servicio elegido, SÓLO hojas del árbol (más precisión).
    // Si no hay servicio elegido, incluir legacy filtrados también.
    const combined = form.solution_tree_service_id
      ? treeLeaves
      : [...legacyFiltered, ...treeLeaves];

    // Dedupe por id
    const seen = new Set();
    return combined.filter((it) => {
      if (seen.has(it.id)) return false;
      seen.add(it.id);
      return true;
    });
  }, [solutionTypes, treeRoots, form.solution_tree_root_id, form.solution_tree_service_id, selectedRoot, servicesOfRoot]);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = {};
      // Diff-based sparse update (sólo enviar campos con cambios reales)
      const diff = (key, orderKey = key, coerce = (v) => v) => {
        const cur = form[key];
        const orig = order[orderKey] ?? "";
        if (cur === orig) return;
        if (cur === "" && (orig == null || orig === "")) return;
        payload[key] = coerce(cur);
      };
      diff("client_name");
      diff("client_phone");
      diff("client_document");
      diff("address");
      // v2.1.4 · Sanitizar lat/lon: solo enviar si es un número válido
      const latNum = form.lat === "" || form.lat === null || form.lat === undefined ? null : Number(form.lat);
      const lonNum = form.lon === "" || form.lon === null || form.lon === undefined ? null : Number(form.lon);
      if (latNum !== null && Number.isFinite(latNum) && latNum !== order.lat) payload.lat = latNum;
      if (lonNum !== null && Number.isFinite(lonNum) && lonNum !== order.lon) payload.lon = lonNum;
      diff("branch_id");
      diff("zone_id");
      diff("observations");
      diff("installation_observations");
      // v2.1.4 · Sanitizar pvp_amount
      if (form.pvp_amount !== "" && form.pvp_amount !== null && form.pvp_amount !== undefined) {
        const p = Number(form.pvp_amount);
        if (Number.isFinite(p) && p !== order.pvp_amount) {
          payload.pvp_amount = p;
        }
      }

      if (form.technician_id && form.technician_id !== (order.technician_id || "")) {
        payload.technician_id = form.technician_id;
      }
      if (form.solution_type_id && form.solution_type_id !== (order.solution_type_id || "")) {
        const st = filteredSolutionTypes.find((s) => s.id === form.solution_type_id)
                || solutionTypes.find((s) => s.id === form.solution_type_id);
        payload.solution_type_id = form.solution_type_id;
        if (st) {
          payload.solution_type_code = st.code;
          payload.solution_type_name = st.name;
        }
        // Si es un nodo del árbol jerárquico (viene con path), guardarlo también
        if (effectiveRules && effectiveRules.node_id === form.solution_type_id) {
          payload.solution_tree_node_id = effectiveRules.node_id;
          payload.solution_tree_path = effectiveRules.path;
        }
      }
      if (form.scheduled_at) {
        const newDt = new Date(form.scheduled_at).toISOString();
        const oldDt = order.scheduled_at ? new Date(order.scheduled_at).toISOString() : null;
        if (newDt !== oldDt) payload.scheduled_at = newDt;
      }
      if (form.priority && form.priority !== order.priority) payload.priority = form.priority;

      if (Object.keys(payload).length === 0) {
        toast.info("Sin cambios que guardar");
        onClose();
        return;
      }

      await api.patch(`/v1/work-orders/${order.id}`, payload);
      toast.success("Orden actualizada");
      onSaved();
    } catch (err) {
      // v2.1.4 · Extraer mensaje de error real de Pydantic (422) o backend genérico
      const detail = err?.response?.data?.detail;
      let msg = "Error al actualizar la orden";
      if (Array.isArray(detail) && detail.length > 0) {
        // Pydantic validation errors → array de {loc, msg, type}
        msg = detail.map((e) => `${(e.loc || []).slice(-1)[0] || "campo"}: ${e.msg}`).join(" · ");
      } else if (typeof detail === "string") {
        msg = detail;
      } else if (detail?.message) {
        msg = detail.message;
      }
      // Log completo para debug en consola
      // eslint-disable-next-line no-console
      console.error("PATCH /work-orders error:", err?.response?.status, err?.response?.data);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const solutionTypeIsSet = !!form.solution_type_id;
  const pvpEditableEffective = !!effectiveRules?.rules?.pvp_configurable;

  return (
    <SlideOverForm
      title={`Editar orden · ${order.code}`}
      breadcrumb={`Órdenes › ${order.code} › Editar`}
      subtitle={order.client_name}
      onClose={onClose}
      width="780px"
      testId="edit-order-slideover"
      footerActions={
        <>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50 text-slate-700 transition"
            data-testid="cancel-edit-order"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="flex-1 px-4 py-2 bg-[#0A3747] text-white text-sm font-semibold rounded-md hover:bg-[#135166] disabled:opacity-40 flex items-center justify-center gap-2 transition shadow-sm"
            data-testid="save-edit-order"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Guardar cambios
          </button>
        </>
      }
    >
      <form onSubmit={submit} className="text-sm" data-testid="edit-order-modal">
        {/* ═══ Sección 1 · CLIENTE + UBICACIÓN CON MAPA (v1.9.2 CX-05) ═══ */}
        <SlideOverSection title="Datos del cliente" icon={UserIcon} description="Editable si necesitas corregir información del titular">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Nombre">
              <input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition" data-testid="edit-order-client-name" />
            </Field>
            <Field label="Documento / RUC">
              <input value={form.client_document} onChange={(e) => setForm({ ...form, client_document: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition" data-testid="edit-order-client-doc" />
            </Field>
            <Field label="Teléfono">
              <input value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition" data-testid="edit-order-client-phone" />
            </Field>
            <Field label="Coordenadas GPS (auto-completa con el mapa)">
              <div className="flex gap-1">
                <input type="number" step="any" value={form.lat}
                  onChange={(e) => setForm({ ...form, lat: e.target.value })}
                  placeholder="Latitud"
                  className="w-full px-2 py-2 border border-slate-300 rounded-md text-xs font-mono focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                  data-testid="edit-order-lat" />
                <input type="number" step="any" value={form.lon}
                  onChange={(e) => setForm({ ...form, lon: e.target.value })}
                  placeholder="Longitud"
                  className="w-full px-2 py-2 border border-slate-300 rounded-md text-xs font-mono focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                  data-testid="edit-order-lon" />
              </div>
            </Field>
          </div>
          <div className="mt-3">
            <Field label="Dirección + marcador en mapa">
              <AddressMapPicker
                address={form.address}
                onAddressChange={(v) => setForm({ ...form, address: v })}
                lat={form.lat}
                lng={form.lon}
                onLocationChange={({ lat, lng }) => setForm({ ...form, lat: String(lat), lon: String(lng) })}
                height={280}
              />
            </Field>
          </div>
        </SlideOverSection>

        {/* ═══ Sección 2 · TIPO DE SOLUCIÓN (3 niveles: Categoría → Servicio → Actividad) ═══ */}
        <SlideOverSection title="Tipo de solución · jerarquía en 3 niveles" icon={Tag} description="Cada nivel filtra el siguiente. Elige de arriba hacia abajo.">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="1 · Categoría principal">
              <select value={form.solution_tree_root_id}
                onChange={(e) => setForm({ ...form, solution_tree_root_id: e.target.value, solution_tree_service_id: "", solution_type_id: "" })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                data-testid="edit-order-root-category">
                <option value="">— Todas —</option>
                {treeRoots.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              {form.solution_tree_root_id && (
                <div className="text-[10px] text-emerald-700 mt-1 font-mono">
                  {servicesOfRoot.length} servicios disponibles
                </div>
              )}
            </Field>
            <Field label="2 · Servicio">
              <select value={form.solution_tree_service_id}
                onChange={(e) => setForm({ ...form, solution_tree_service_id: e.target.value, solution_type_id: "" })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
                disabled={!form.solution_tree_root_id || servicesOfRoot.length === 0}
                data-testid="edit-order-service">
                <option value="">
                  {!form.solution_tree_root_id
                    ? "Elige categoría primero"
                    : servicesOfRoot.length === 0
                      ? "— Sin servicios —"
                      : "— Todos —"}
                </option>
                {servicesOfRoot.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              {form.solution_tree_service_id && (() => {
                const svc = servicesOfRoot.find((s) => s.id === form.solution_tree_service_id);
                if (!svc) return null;
                const leafCount = collectLeaves(svc).length;
                return (
                  <div className="text-[10px] text-emerald-700 mt-1 font-mono">
                    {leafCount} actividad{leafCount !== 1 ? "es" : ""} en este servicio
                  </div>
                );
              })()}
            </Field>
            <Field label="3 · Actividad específica">
              <select value={form.solution_type_id} onChange={(e) => setForm({ ...form, solution_type_id: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition disabled:bg-slate-50 disabled:text-slate-400"
                disabled={loadingRefs || (!form.solution_tree_root_id && solutionTypes.length === 0)}
                data-testid="edit-order-solution-type">
                <option value="">— Sin especificar —</option>
                {filteredSolutionTypes.map((s) => {
                  // v1.9.5 Bug QA 22-07 #1: sólo mostrar el nombre limpio (sin códigos técnicos NODE-xxx)
                  const isTreeNode = s._fromTree === true;
                  const label = isTreeNode
                    ? s.name
                    : (s.code ? `${s.code} · ${s.name}` : s.name);
                  return <option key={s.id} value={s.id}>{label}</option>;
                })}
              </select>
              <div className="text-[10px] text-slate-500 mt-1 font-mono">
                {filteredSolutionTypes.length} opcion{filteredSolutionTypes.length !== 1 ? "es" : ""} disponible{filteredSolutionTypes.length !== 1 ? "s" : ""}
              </div>
            </Field>
          </div>
          {effectiveRules && (
            <div className="mt-3 rounded-md bg-emerald-50 border border-emerald-200 p-3 text-[11px]">
              <div className="text-[10px] font-mono uppercase tracking-wider text-emerald-800 mb-1.5 flex items-center gap-1">
                ✓ Reglas efectivas · cascada aplicada · pasá el mouse sobre cada campo
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-emerald-900">
                {[
                  ["SLA P1", "sla_p1_hours", "h"],
                  ["SLA P2", "sla_p2_hours", "h"],
                  ["SLA P3", "sla_p3_hours", "h"],
                  ["Tecnología", "tecnologia", ""],
                  ["Agenda", "scheduling_mode", ""],
                  ["IA+GPS", "ai_gps_validation", "bool"],
                  ["WhatsApp", "whatsapp_confirmation", "bool"],
                  ["PVP editable", "pvp_configurable", "bool"],
                ].map(([label, field, suffix]) => {
                  const src = effectiveRules.sources?.[field];
                  const title = src
                    ? `Heredado de "${src.name}" (nivel ${src.depth})`
                    : "Valor por defecto del sistema (ningún ancestro configurado)";
                  const v = effectiveRules.rules[field];
                  const display = suffix === "bool" ? (v ? "Sí" : "No") : `${v}${suffix}`;
                  return (
                    <div key={field} title={title} className="cursor-help border-b border-dashed border-emerald-300 pb-0.5">
                      {label}: <b>{display}</b>
                      {src && <span className="text-[9px] text-emerald-600 ml-1">· {src.name.slice(0, 12)}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </SlideOverSection>

        {/* ═══ Sección 3 · PROGRAMACIÓN ═══ */}
        <SlideOverSection title="Programación" icon={CalendarClock} description="Técnico, fecha planificada y prioridad">
          {/* v1.9.5 · Bug QA 22-07 #4: Restricción de cambio de técnico según estado */}
          {(() => {
            const LOCKED_TECH_STATES = ["asignada", "en_ruta", "en_ejecucion", "pendiente_fiscalizacion"];
            const techLocked = LOCKED_TECH_STATES.includes(order.status);
            return (
              <>
                {techLocked && (
                  <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-[11px] mb-3 flex items-start gap-2" data-testid="tech-locked-banner">
                    <AlertTriangle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-amber-900 mb-0.5">Técnico bloqueado en este estado</div>
                      <div className="text-amber-800 leading-relaxed">
                        La orden está en <b>{STATUS_LABELS[order.status] || order.status}</b>. Para reasignar técnico primero devuelve la orden a
                        <b> Pendiente de agendamiento</b> (transición manual).
                      </div>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Técnico asignado">
                    <select value={form.technician_id} onChange={(e) => setForm({ ...form, technician_id: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition disabled:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-500"
                      disabled={loadingRefs || techLocked} data-testid="edit-order-technician">
                      <option value="">— Sin asignar —</option>
                      {techs.map((t) => (
                        <option key={t.id} value={t.id}>{t.name} · {t.role_display || t.role}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Fecha planificada">
                    <input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition" data-testid="edit-order-scheduled-at" />
                    {!form.technician_id && form.scheduled_at && (
                      <div className="text-[10px] text-amber-700 mt-1 flex items-center gap-1">
                        <AlertTriangle size={10} /> Sin técnico asignado, la orden permanecerá en <b>Pte. agendamiento</b>
                      </div>
                    )}
                  </Field>
                  <Field label="Prioridad">
                    <div className="grid grid-cols-3 gap-2">
                      {["P1", "P2", "P3"].map((p) => (
                        <button key={p} type="button" onClick={() => setForm({ ...form, priority: p })}
                          className={`py-1.5 text-xs font-bold rounded-md border transition ${form.priority === p ? "bg-[#0A3747] text-white border-[#0A3747] shadow-sm" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}
                          data-testid={`edit-order-priority-${p}`}>
                          {p}{effectiveRules && effectiveRules.rules[`sla_${p.toLowerCase()}_hours`] ? ` · ${effectiveRules.rules[`sla_${p.toLowerCase()}_hours`]}h` : ""}
                        </button>
                      ))}
                    </div>
                  </Field>
                </div>
              </>
            );
          })()}
        </SlideOverSection>

        {/* ═══ Sección 4 · FINANCIERO (PVP) ═══ */}
        {/* v1.9.1 · siempre visible, con banner explicativo si el tipo NO tiene pvp_configurable */}
        <SlideOverSection title="Financiero · PVP al cliente" icon={DollarSign} description="Precio de venta al cliente final (opcional según tipo)">
          {!solutionTypeIsSet ? (
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-[11px] text-slate-600 flex items-start gap-2">
              <AlertTriangle size={14} className="text-slate-400 flex-shrink-0 mt-0.5" />
              <div>Selecciona primero un <b>Tipo de solución</b> para determinar si el PVP es editable en esta orden.</div>
            </div>
          ) : pvpEditableEffective ? (
            <Field label="PVP cliente (USD)">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-mono">USD</span>
                <input
                  type="number" step="0.01" min="0"
                  value={form.pvp_amount}
                  onChange={(e) => setForm({ ...form, pvp_amount: e.target.value })}
                  className="w-full pl-12 pr-3 py-2 border-2 border-[#E0E621] rounded-md text-sm font-mono text-right bg-yellow-50/40 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                  placeholder="0.00"
                  data-testid="edit-order-pvp"
                />
              </div>
              <div className="text-[10px] text-emerald-700 mt-1 flex items-center gap-1">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                Este tipo de solución permite establecer un PVP personalizado
              </div>
            </Field>
          ) : (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-[11px]" data-testid="pvp-disabled-banner">
              <div className="flex items-start gap-2">
                <AlertTriangle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-amber-900 mb-0.5">PVP no editable para este tipo de solución</div>
                  <div className="text-amber-800 leading-relaxed">
                    El tipo <b>{filteredSolutionTypes.find(s => s.id === form.solution_type_id)?.name || "seleccionado"}</b> tiene el toggle{" "}
                    <b>“PVP configurable en orden”</b> desactivado en la cascada de reglas. Se usará el precio por defecto de la actividad.
                    <br />
                    Para habilitarlo, ve a <b>Tipos de solución → Editar reglas</b> del nodo correspondiente y activa el switch <b>PVP configurable en orden</b>.
                  </div>
                </div>
              </div>
            </div>
          )}
        </SlideOverSection>

        {/* ═══ Sección 5 · NOTAS ═══ */}
        <SlideOverSection title="Observaciones" icon={ClipboardList} description="Notas del agendador e instrucciones para el técnico">
          <Field label="Observaciones del agendador">
            <textarea value={form.observations} onChange={(e) => setForm({ ...form, observations: e.target.value })}
              rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm resize-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
              data-testid="edit-order-observations" placeholder="Notas para el técnico o el agendador…" />
          </Field>
          <div className="mt-3" />
          <Field label="Observaciones de instalación">
            <textarea value={form.installation_observations} onChange={(e) => setForm({ ...form, installation_observations: e.target.value })}
              rows={2} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm resize-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
              data-testid="edit-order-inst-observations" placeholder="Notas técnicas de instalación…" />
          </Field>
        </SlideOverSection>
      </form>
    </SlideOverForm>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-wider text-gray-500 mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}
